<template>
  <CatalogoPagina
    titulo="Usuarios"
    prefijo="usuarios"
    :resumen="resumen"
    :columnas="columnas"
    :total="total"
    :pagina="paginaActual"
    :total-paginas="totalPaginas"
    :limite="limite"
    :cargando="cargando"
    :ya-cargo="yaCargo"
    :error="errorCarga"
    mensaje-error="No pudimos cargar los usuarios."
    :orden-campo="ordenCampo"
    :orden-asc="ordenAsc"
    :aviso="aviso"
    @limite="alCambiarLimite"
    @ordenar="ordenarPor"
    @pagina="cambiarPagina"
    @reintentar="initialize"
    @cerrar-aviso="aviso = ''"
  >
    <template #acciones>
      <button type="button" class="rm-btn rm-btn-primario" data-testid="usuarios-nuevo" @click="nuevoUsuario">
        <AppIcon name="plus" size="h-4 w-4" />
        Nuevo usuario
      </button>
    </template>

    <template #filtros>
      <AppBusqueda
        v-model="busqueda"
        placeholder="Buscar usuario"
        testid="usuarios-buscar"
        @buscar="aplicarFiltros"
      />
    </template>

    <template #filas>
      <tr v-for="usuario in usuarios" :key="usuario._id" class="us-fila" data-testid="usuario-fila">
        <td>
          <div class="cat-nombre">
            <span class="cat-avatar" aria-hidden="true">{{ inicial(usuario) }}</span>
            <div class="cat-nombre-datos">
              <span class="cat-nombre-texto us-usuario">
                {{ usuario.user }}
                <span v-if="esYo(usuario)" class="us-yo">Tú</span>
              </span>
              <span class="cat-meta">{{ nombreDe(usuario) }}</span>
            </div>
          </div>
        </td>
        <td class="us-correo">
          <template v-if="usuario.email">{{ usuario.email }}</template>
          <span v-else class="cat-sin-dato" aria-label="Sin correo">—</span>
        </td>
        <td>
          <span class="us-rol" :class="{ super: esSuper(usuario.role) }">{{ etiquetaRol(usuario.role) }}</span>
        </td>
        <td>
          <template v-if="usuario.employee">{{ nombreDePersona(usuario.employee) }}</template>
          <span v-else class="cat-sin-dato"><span aria-hidden="true">—</span><span class="us-sin">Sin empleado</span></span>
        </td>
        <td class="us-alta">{{ fecha(usuario.createdAt) }}</td>
        <td class="cat-fila-acciones">
          <button
            type="button"
            class="cat-icono-btn"
            :aria-label="'Editar a ' + usuario.user"
            data-testid="usuario-editar"
            @click="editarUsuario(usuario)"
          >
            <AppIcon name="pencil" size="h-4 w-4" />
          </button>
          <button
            type="button"
            class="cat-icono-btn peligro"
            :aria-label="esYo(usuario) ? 'No puedes darte de baja a ti mismo' : 'Dar de baja a ' + usuario.user"
            :title="esYo(usuario) ? 'No puedes darte de baja a ti mismo' : ''"
            :disabled="esYo(usuario)"
            data-testid="usuario-eliminar"
            @click="pedirEliminar(usuario)"
          >
            <AppIcon name="trash" size="h-4 w-4" />
          </button>
        </td>
      </tr>
    </template>

    <template #vacio>
      <AppIcon :name="busqueda.trim() ? 'search' : 'lock'" size="h-8 w-8" />
      <p v-if="busqueda.trim()">No hay usuarios que coincidan con “{{ busqueda.trim() }}”.</p>
      <template v-else>
        <p>Aún no hay usuarios.</p>
        <button type="button" class="rm-btn rm-btn-primario" @click="nuevoUsuario">Crear el primero</button>
      </template>
    </template>

    <BaseModal :visible="dialogoForm" :title="tituloForm" max-width="max-w-[720px]" persistent @close="cerrarFormulario">
      <form novalidate class="us-form" data-testid="usuario-form" @submit.prevent="guardarUsuario">
        <AppField
          v-model="form.name"
          label="Nombre"
          required
          placeholder="Alejandra Esperano"
          :error="mostrarError('name')"
          testid="usuario-nombre"
          @blur="alSalirDeNombre"
        />
        <AppField
          v-model="form.user"
          label="Usuario"
          required
          uppercase
          placeholder="ALEJANDRA"
          :error="mostrarError('user')"
          testid="usuario-usuario"
          @blur="tocar('user')"
        />
        <AppField v-slot="{ controlId }" label="Correo" required :error="mostrarError('email')" testid="usuario-correo">
          <AppCorreo
            :id="controlId"
            v-model="form.email"
            :invalid="Boolean(mostrarError('email'))"
            testid="usuario-correo"
            @blur="tocar('email')"
          />
        </AppField>
        <AppField v-slot="{ controlId }" label="Contraseña" :required="!usuarioSeleccionado" :error="mostrarError('pwd')" testid="usuario-contrasena">
          <div class="us-contrasena">
            <input
              :id="controlId"
              v-model="form.pwd"
              class="app-field-control"
              :type="verContrasena ? 'text' : 'password'"
              autocomplete="new-password"
              :placeholder="usuarioSeleccionado ? 'Déjala vacía para no cambiarla' : 'Mínimo 8 caracteres'"
              :aria-invalid="mostrarError('pwd') ? 'true' : undefined"
              data-testid="usuario-contrasena"
              @blur="tocar('pwd')"
            />
            <button
              type="button"
              class="us-ver"
              :aria-label="verContrasena ? 'Ocultar contraseña' : 'Mostrar contraseña'"
              :aria-pressed="verContrasena ? 'true' : 'false'"
              data-testid="usuario-ver-contrasena"
              @click="verContrasena = !verContrasena"
            >
              <AppIcon :name="verContrasena ? 'eye-off' : 'eye'" size="h-4 w-4" />
            </button>
          </div>
        </AppField>
        <AppField v-slot="{ controlId }" label="Rol" required :error="mostrarError('role')" testid="usuario-rol">
          <select :id="controlId" v-model="form.role" :class="['app-field-control us-select', { vacio: !form.role }]" data-testid="usuario-rol" @blur="tocar('role')">
            <option value="" disabled>Elige un rol</option>
            <option v-for="rol in roles" :key="rol._id" :value="rol._id">{{ etiquetaRol(rol) }}</option>
          </select>
        </AppField>
        <AppField v-slot="{ controlId }" label="Empleado" required :error="mostrarError('employee')" testid="usuario-empleado">
          <select
            :id="controlId"
            v-model="form.employee"
            :class="['app-field-control us-select', { vacio: !form.employee }]"
            data-testid="usuario-empleado"
            @blur="tocar('employee')"
          >
            <option value="" disabled>¿Qué empleado es?</option>
            <option v-for="empleado in empleados" :key="empleado._id" :value="empleado._id">{{ nombreDePersona(empleado) }}</option>
          </select>
        </AppField>
      </form>
      <template #footer>
        <button type="button" class="rm-btn rm-btn-secundario" @click="cerrarFormulario">Cancelar</button>
        <button
          type="button"
          class="rm-btn rm-btn-primario"
          :disabled="guardando"
          data-testid="usuario-guardar"
          @click="guardarUsuario"
        >
          {{ guardando ? 'Guardando…' : 'Guardar' }}
        </button>
      </template>
    </BaseModal>

    <BaseModal :visible="dialogoEliminar" title="Dar de baja usuario" @close="cerrarEliminar">
      <p class="cat-confirmacion">
        ¿Dar de baja a <b>{{ usuarioSeleccionado ? usuarioSeleccionado.user : '' }}</b>? Ya no podrá entrar al sistema.
        Lo que haya registrado no cambia.
      </p>
      <template #footer>
        <button type="button" class="rm-btn rm-btn-secundario" @click="cerrarEliminar">Cancelar</button>
        <button
          type="button"
          class="rm-btn rm-btn-peligro"
          :disabled="guardando"
          data-testid="usuario-confirmar-eliminar"
          @click="confirmarEliminar"
        >
          {{ guardando ? 'Dando de baja…' : 'Dar de baja' }}
        </button>
      </template>
    </BaseModal>
  </CatalogoPagina>
</template>

<script>
/*
 * Usuarios — catálogo migrado sobre CatalogoPagina.
 *
 * Paginación, búsqueda (usuario, nombre o correo) y orden en el backend
 * (/api/users/paginated), que nunca regresa la contraseña y, como antes, no
 * le muestra los superAdmin a un Admin. Se ve el empleado de cada usuario.
 * La contraseña se pide al crear; al editar es opcional (vacía se conserva,
 * antes había que volver a escribirla cada vez). Nadie puede darse de baja
 * a sí mismo. Dar de baja es lógico (status 0).
 */
import ApiService from '@/services/apis'
import AppBusqueda from '@/components/ui/AppBusqueda.vue'
import AppCorreo from '@/components/ui/AppCorreo.vue'
import AppField from '@/components/ui/AppField.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import BaseModal from '@/components/ui/BaseModal.vue'
import CatalogoPagina, { LIMITE_BASE } from '@/components/catalogo/CatalogoPagina.vue'
import { validateNoErrors } from '@/helpers/helpers'
import { capitalizarNombre, formatearFechaNumerica, nombreDePersona } from '@/utils/formato'
import { paginaEquivalente } from '@/utils/paginacion'

export const LARGO_MINIMO_CONTRASENA = 8

const ROLES = { superAdmin: 'Súper administrador', Admin: 'Administrador' }

export function etiquetaRol(rol) {
  const nombre = rol && typeof rol === 'object' ? rol.name : rol
  if (!nombre) return 'Sin rol'
  return ROLES[nombre] || capitalizarNombre(nombre)
}

function idDe(valor) {
  return valor && typeof valor === 'object' ? valor._id || '' : valor || ''
}

function formularioVacio() {
  return { name: '', user: '', email: '', pwd: '', role: '', employee: '' }
}

export default {
  name: 'usuarios',
  components: { AppBusqueda, AppCorreo, AppField, AppIcon, BaseModal, CatalogoPagina },
  data() {
    return {
      usuarios: [],
      roles: [],
      empleados: [],
      busqueda: '',
      ordenCampo: 'user',
      ordenAsc: true,
      paginaActual: 1,
      total: 0,
      totalPaginas: 1,
      limite: LIMITE_BASE,
      cargando: true,
      yaCargo: false,
      errorCarga: false,
      guardando: false,
      dialogoForm: false,
      dialogoEliminar: false,
      usuarioSeleccionado: null,
      form: formularioVacio(),
      verContrasena: false,
      tocados: {},
      intentoGuardar: false,
      aviso: '',
      sesion: JSON.parse(sessionStorage.getItem('sesion') || 'null'),
    }
  },
  computed: {
    columnas() {
      return [
        { value: 'user', label: 'Usuario' },
        { value: 'email', label: 'Correo', ordenable: false },
        { value: 'role', label: 'Rol', ancho: '190px' },
        { value: 'empleado', label: 'Empleado', ordenable: false },
        { value: 'createdAt', label: 'Alta', ancho: '120px' },
      ]
    },
    resumen() {
      if (this.busqueda.trim()) return `${this.total} ${this.total === 1 ? 'coincidencia' : 'coincidencias'}`
      return `${this.total} ${this.total === 1 ? 'usuario activo' : 'usuarios activos'}`
    },
    tituloForm() {
      return this.usuarioSeleccionado ? `Editar a ${this.usuarioSeleccionado.user}` : 'Nuevo usuario'
    },
    errores() {
      const f = this.form
      const e = {}
      if (!f.name.trim()) e.name = 'Requerido.'
      if (!f.user.trim()) e.user = 'Requerido.'
      else if (/\s/.test(f.user.trim())) e.user = 'Sin espacios.'
      if (!f.email) e.email = 'Requerido.'
      else if (!validateNoErrors('email', f.email)) e.email = 'Formato no válido.'
      if (!this.usuarioSeleccionado && !f.pwd) e.pwd = 'Requerida.'
      else if (f.pwd && f.pwd.length < LARGO_MINIMO_CONTRASENA) e.pwd = `Mínimo ${LARGO_MINIMO_CONTRASENA} caracteres.`
      if (!f.role) e.role = 'Requerido.'
      if (!f.employee) e.employee = 'Requerido.'
      return e
    },
  },
  created() {
    this.ultimaConsulta = 0
    this.limiteListo = false
    this.cargarCatalogos()
  },
  beforeDestroy() {
    clearTimeout(this.avisoTimer)
  },
  methods: {
    etiquetaRol,
    nombreDePersona,
    fecha(valor) {
      return formatearFechaNumerica(valor)
    },
    esYo(usuario) {
      return Boolean(this.sesion && usuario && this.sesion._id === usuario._id)
    },
    esSuper(rol) {
      return Boolean(rol && rol.name === 'superAdmin')
    },
    inicial(usuario) {
      return String(usuario.user || '?').trim().charAt(0).toUpperCase()
    },
    nombreDe(usuario) {
      return capitalizarNombre(String(usuario.name || '').trim())
    },
    async cargarCatalogos() {
      try {
        const [roles, empleados] = await Promise.all([ApiService.roles(), ApiService.empleado()])
        this.roles = (roles && roles.data) || []
        this.empleados = ((empleados && empleados.data) || []).slice().sort((a, b) =>
          nombreDePersona(a).localeCompare(nombreDePersona(b), 'es')
        )
      } catch (err) {
        // El formulario pide elegir rol y empleado: sin catálogos no deja guardar
      }
    },
    async initialize() {
      const consulta = ++this.ultimaConsulta
      this.cargando = true
      this.errorCarga = false
      try {
        const res = await ApiService.usuarioPaginado({
          pagina: this.paginaActual,
          limite: this.limite,
          busqueda: this.busqueda.trim(),
          ordenarPor: this.ordenCampo,
          direccion: this.ordenAsc ? 'asc' : 'desc',
        })
        if (consulta !== this.ultimaConsulta) return
        if (!Array.isArray(res?.usuarios)) throw new Error('Respuesta inválida del servicio')
        this.usuarios = res.usuarios
        this.total = Number(res.total) || 0
        this.totalPaginas = Math.max(1, Number(res.totalPages) || 1)
        this.yaCargo = true
        if (this.paginaActual > this.totalPaginas) {
          this.paginaActual = this.totalPaginas
          return this.initialize()
        }
      } catch (err) {
        if (consulta !== this.ultimaConsulta) return
        this.errorCarga = true
        this.yaCargo = false
        this.usuarios = []
        this.total = 0
      } finally {
        if (consulta === this.ultimaConsulta) this.cargando = false
      }
    },
    alCambiarLimite(nuevo) {
      if (!this.limiteListo) {
        this.limiteListo = true
        this.limite = nuevo
        return this.initialize()
      }
      if (nuevo === this.limite) return
      this.paginaActual = paginaEquivalente(this.paginaActual, this.limite, nuevo)
      this.limite = nuevo
      this.initialize()
    },
    ordenarPor(campo) {
      if (campo === this.ordenCampo) {
        this.ordenAsc = !this.ordenAsc
      } else {
        this.ordenCampo = campo
        // El alta empieza por la más reciente; lo demás, de la A
        this.ordenAsc = campo !== 'createdAt'
      }
      this.aplicarFiltros()
    },
    aplicarFiltros() {
      this.paginaActual = 1
      this.initialize()
    },
    cambiarPagina(pagina) {
      if (pagina < 1 || pagina > this.totalPaginas || pagina === this.paginaActual) return
      this.paginaActual = pagina
      this.initialize()
    },
    abrirFormulario(usuario) {
      this.usuarioSeleccionado = usuario
      this.form = usuario
        ? {
            name: this.nombreDe(usuario),
            user: String(usuario.user || '').trim(),
            email: usuario.email || '',
            pwd: '',
            role: idDe(usuario.role),
            employee: idDe(usuario.employee),
          }
        : formularioVacio()
      this.verContrasena = false
      this.tocados = {}
      this.intentoGuardar = false
      this.dialogoForm = true
    },
    nuevoUsuario() {
      this.abrirFormulario(null)
    },
    editarUsuario(usuario) {
      this.abrirFormulario(usuario)
    },
    cerrarFormulario() {
      this.dialogoForm = false
      this.usuarioSeleccionado = null
      this.form.pwd = ''
    },
    tocar(campo) {
      this.tocados = { ...this.tocados, [campo]: true }
    },
    mostrarError(campo) {
      return this.tocados[campo] || this.intentoGuardar ? this.errores[campo] || '' : ''
    },
    alSalirDeNombre() {
      this.form.name = capitalizarNombre(this.form.name)
      this.tocar('name')
    },
    async guardarUsuario() {
      this.intentoGuardar = true
      if (Object.keys(this.errores).length || this.guardando) return
      const f = this.form
      const payload = {
        name: capitalizarNombre(f.name),
        user: f.user.trim().toUpperCase(),
        email: f.email.trim(),
        role: f.role,
        employee: f.employee,
      }
      // Vacía al editar = se conserva la contraseña actual
      if (f.pwd) payload.pwd = f.pwd
      const editando = Boolean(this.usuarioSeleccionado)
      this.guardando = true
      try {
        if (editando) await ApiService.usuarioEditar(this.usuarioSeleccionado._id, payload)
        else await ApiService.usuarioNuevo(payload)
        this.cerrarFormulario()
        await this.initialize()
        this.mostrarAviso(editando ? 'Usuario actualizado' : 'Usuario creado')
      } catch (err) {
        // El servicio ya muestra el motivo (p. ej. "Usuario ya existe"); el formulario sigue abierto
      } finally {
        this.guardando = false
      }
    },
    pedirEliminar(usuario) {
      if (this.esYo(usuario)) return
      this.usuarioSeleccionado = usuario
      this.dialogoEliminar = true
    },
    cerrarEliminar() {
      this.dialogoEliminar = false
      this.usuarioSeleccionado = null
    },
    async confirmarEliminar() {
      this.guardando = true
      try {
        await ApiService.usuarioEditar(this.usuarioSeleccionado._id, { status: 0 })
        const nombre = this.usuarioSeleccionado.user
        this.cerrarEliminar()
        if (this.usuarios.length === 1 && this.paginaActual > 1) this.paginaActual--
        await this.initialize()
        this.mostrarAviso(`${nombre} dado de baja`)
      } catch (err) {
        // El servicio ya muestra el motivo
      } finally {
        this.guardando = false
      }
    },
    mostrarAviso(mensaje) {
      this.aviso = mensaje
      clearTimeout(this.avisoTimer)
      this.avisoTimer = setTimeout(() => {
        this.aviso = ''
      }, 4500)
    },
  },
}
</script>

<style scoped>
.us-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 14px;
}
.us-usuario {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.us-yo {
  padding: 1px 7px;
  border-radius: 999px;
  background: #edf4fc;
  color: #0a4f9c;
  font-size: 11px;
  font-weight: 700;
}
.us-correo {
  color: #3d4b5f;
}
.us-rol {
  display: inline-flex;
  padding: 2px 9px;
  border-radius: 999px;
  background: #f1f4f8;
  color: #3d4b5f;
  font-size: 12px;
  font-weight: 700;
}
.us-rol.super {
  background: #12304f;
  color: #fff;
}
.us-alta {
  color: #3d4b5f;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.us-contrasena {
  position: relative;
}
.us-contrasena .app-field-control {
  padding-right: 44px;
}
.us-ver {
  position: absolute;
  top: 50%;
  right: 6px;
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #3d4b5f;
  cursor: pointer;
  transform: translateY(-50%);
}
.us-ver:hover {
  background: #edf3f8;
}
.us-ver:focus-visible {
  outline: 3px solid #7ab9f2;
  outline-offset: 1px;
}
.us-select {
  appearance: none;
  padding-right: 36px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%233d4b5f' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M19.5 8.25l-7.5 7.5-7.5-7.5'/%3E%3C/svg%3E");
  background-position: right 12px center;
  background-repeat: no-repeat;
  background-size: 16px;
  cursor: pointer;
}
/* Sin elegir: el texto de ayuda se ve como ayuda, no como valor */
.us-select.vacio {
  color: #8a97a8;
}
.us-select option {
  color: #0b1b2e;
}
.us-sin {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
@media (max-width: 700px) {
  .us-form {
    grid-template-columns: minmax(0, 1fr);
  }
  .us-sin {
    position: static;
    width: auto;
    height: auto;
    clip: auto;
  }
}
</style>
